// src/components/Admin/Educasimulator/SimulatorForm/SimulatorForm.js
import React, { useCallback, useEffect, useState } from "react";
import { Form, Image } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./SimulatorForm.form";
import { simulatorApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./SimulatorForm.scss";

// Convierte "a,b,c" -> ["a","b","c"] para 'data' (opcional)
const toArray = (val) =>
  typeof val === "string" && val.trim()
    ? val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

export function SimulatorForm(props) {
  const { onClose, onReload, simulator } = props; // en edición puede venir parcial
  const { accessToken } = useAuth();

  const [loaded, setLoaded] = useState(false);
  const [fullSim, setFullSim] = useState(simulator || null);

  // Si estoy editando, traigo el simulador completo para asegurar traer 'tag'
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (simulator?._id) {
          const res = await simulatorApi.getSimulatorById(
            accessToken,
            simulator._id,
          );
          if (!ignore) {
            setFullSim(res.simulator || simulator);
            setLoaded(true);
          }
        } else {
          setLoaded(true);
        }
      } catch (_e) {
        // Si falla, sigo con lo que venía para no bloquear el form
        setFullSim(simulator || null);
        setLoaded(true);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [simulator, accessToken]);

  const formik = useFormik({
    initialValues: initialValues(fullSim),
    enableReinitialize: true, // MUY importante cuando cargamos datos async
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue, helpers) => {
      try {
        // Lo que enviamos:
        // - data como array (si el usuario tecleó CSV)
        // - tag se envía tal cual (string)
        const payload = {
          title: formValue.title || "",
          description: formValue.description || "",
          idServer: formValue.idServer || "",
          data: toArray(formValue.data),
          tag: formValue.tag ?? "", // <- aseguramos que exista aunque esté vacío
        };

        // miniatura (archivo) si el usuario la cambió
        if (formik.values.miniatureFile) {
          payload.miniatureFile = formik.values.miniatureFile;
        }

        if (!fullSim?._id) {
          await simulatorApi.createSimulator(accessToken, payload);
        } else {
          await simulatorApi.updateSimulator(accessToken, fullSim._id, payload);
        }

        onReload?.();
        onClose?.();
      } catch (error) {
        console.error("[SimulatorForm] submit error:", error);
        helpers.setSubmitting(false);
      }
    },
  });

  // Manejo de drop de imagen
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (!file) return;

      const valid = ["image/jpeg", "image/png"];
      if (!valid.includes(file.type)) return;

      formik.setFieldValue("miniature", URL.createObjectURL(file));
      formik.setFieldValue("miniatureFile", file);
    },
    [formik],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop,
    multiple: false,
  });

  const getMiniature = () => {
    if (formik.values.miniatureFile) return formik.values.miniature;
    if (formik.values.miniature) return `${formik.values.miniature}`; // url remota
    return null;
    // si quieres mostrar un placeholder aquí, devuélvelo en vez de null
  };

  if (!loaded) return null;

  return (
    <Form className="simulator-form" onSubmit={formik.handleSubmit}>
      <div className="simulator-form__miniature" {...getRootProps()}>
        <input {...getInputProps()} />
        {getMiniature() ? (
          <Image size="small" src={getMiniature()} />
        ) : (
          <div>
            <span>Arrastra tu miniatura</span>
          </div>
        )}
      </div>

      <Form.Input
        name="title"
        placeholder="Título del simulador"
        onChange={formik.handleChange}
        value={formik.values.title}
        error={formik.errors.title}
      />

      <Form.TextArea
        name="description"
        placeholder="Descripción del simulador"
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
      />

      <Form.Input
        name="idServer"
        placeholder="ID/Clave de servidor (opcional)"
        onChange={formik.handleChange}
        value={formik.values.idServer}
        error={formik.errors.idServer}
      />

      <Form.Input
        name="data"
        placeholder='Datos (opcional), separados por comas, ej: "m1,m2,m3"'
        onChange={formik.handleChange}
        value={formik.values.data}
        error={formik.errors.data}
      />

      {/* Campo tag como STRING */}
      <Form.Input
        name="tag"
        placeholder='Etiqueta (opcional), ej: "Informática"'
        onChange={formik.handleChange}
        value={formik.values.tag}
      />

      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {!fullSim?._id ? "Crear simulador" : "Actualizar simulador"}
      </Form.Button>
    </Form>
  );
}
